import { useSuspenseQuery } from '@apollo/client';
import { ErrorBoundary } from '@sentry/react';
import { FC, Suspense } from 'react';
import { useParams } from 'react-router-dom';

import { SettingsPageFrame } from '@eluve/blocks';
import {
  Card,
  EluveCardContent,
  Grid,
  LoadingIndicator,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { FeatureFlaggedComponent } from '@eluve/smart-blocks';
import { formatHumanName } from '@eluve/utils';

import { MemberFhirMirrorCard } from './MemberFhirMirrorCard';
import { getTenantMemberQuery } from './operations';

const DetailCell: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <Stack gap={0} align="start">
    <Text size="xs" color="tertiary">
      {label}
    </Text>
    <Text size="sm" color={value ? 'primary' : 'disabled'}>
      {value || '—'}
    </Text>
  </Stack>
);

const MemberDetail: FC<{ userId: string }> = ({ userId }) => {
  const tenantId = useAssignedTenantIdFromParams();
  const { t } = useTranslation();

  const { data } = useSuspenseQuery(getTenantMemberQuery, {
    variables: {
      filter: {
        _and: [{ tenantId: { _eq: tenantId } }, { userId: { _eq: userId } }],
      },
    },
  });
  const member = data.combinedTenantUsersInvites[0];

  const title = member
    ? formatHumanName(
        member.firstName ?? undefined,
        member.lastName ?? undefined,
      ) ||
      member.email ||
      ''
    : '';

  return (
    <SettingsPageFrame
      size="wide"
      parentTitle={t('tenantAdmin.users.membersTitle')}
      backTo={`/organizations/${tenantId}/settings/members`}
      title={title}
    >
      {!member ? (
        <Text color="tertiary">{t('tenantAdmin.common.thereWasAnError')}</Text>
      ) : (
        <Stack gap={6} align="stretch">
          <Card className="w-full rounded-xl border-border-primary">
            <EluveCardContent>
              <Grid columns={1} className="gap-x-6 gap-y-3 sm:grid-cols-2">
                <DetailCell
                  label={t('tenantAdmin.users.emailColumn')}
                  value={member.email}
                />
                <DetailCell
                  label={t('tenantAdmin.users.roleColumn')}
                  value={
                    member.role === 'tenant-admin'
                      ? t('tenantAdmin.users.roleAdmin')
                      : t('tenantAdmin.users.roleMember')
                  }
                />
                <DetailCell
                  label={t('tenantAdmin.users.lastSeenColumn')}
                  value={
                    member.lastAccessedAt
                      ? new Date(member.lastAccessedAt).toLocaleDateString()
                      : null
                  }
                />
                <DetailCell
                  label={t('tenantAdmin.users.joinInviteDateColumn')}
                  value={
                    member.createdAt
                      ? new Date(member.createdAt).toLocaleDateString()
                      : null
                  }
                />
              </Grid>
            </EluveCardContent>
          </Card>
          <FeatureFlaggedComponent flag="MEDPLUM">
            <MemberFhirMirrorCard userId={userId} />
          </FeatureFlaggedComponent>
        </Stack>
      )}
    </SettingsPageFrame>
  );
};

export const MemberDetailPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { t } = useTranslation();

  if (!userId) return null;

  return (
    <ErrorBoundary
      fallback={<div>{t('tenantAdmin.common.thereWasAnError')}</div>}
    >
      <Suspense fallback={<LoadingIndicator size="xl" />}>
        <MemberDetail userId={userId} />
      </Suspense>
    </ErrorBoundary>
  );
};
